import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ClipboardList, Loader2 } from "lucide-react";

export default function TaskFormDialog({
  open,
  onOpenChange,
  task = null,
  defaultDate = "",
  defaultProjectId = "",
  lockProject = false,
  onSuccess,
}) {
  const isEdit = !!task;
  const [form, setForm] = useState({
    title: "", description: "", date: defaultDate,
    project_id: defaultProjectId, assignee_id: "",
    quantity: "", location: "",
  });
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(task
      ? {
          title: task.title || "",
          description: task.description || "",
          date: task.date || defaultDate,
          project_id: task.project_id || defaultProjectId,
          assignee_id: task.assignee_id || "",
          quantity: task.quantity ?? "",
          location: task.location || "",
        }
      : {
          title: "", description: "", date: defaultDate,
          project_id: defaultProjectId, assignee_id: "",
          quantity: "", location: "",
        }
    );
    Promise.all([
      api.get("/users"),
      api.get("/projects", { params: { archived: false } }),
    ]).then(([u, p]) => { setUsers(u.data); setProjects(p.data); });
  }, [open, task, defaultDate, defaultProjectId]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.date) return toast.error("Date is required");
    setBusy(true);
    try {
      const body = {
        title: form.title.trim(),
        description: form.description,
        date: form.date,
        project_id: form.project_id || null,
        assignee_id: form.assignee_id || null,
        quantity: form.quantity === "" ? null : Number(form.quantity),
        location: form.location,
      };
      if (isEdit) {
        await api.patch(`/tasks/${task.id}`, body);
        toast.success("Task updated");
      } else {
        await api.post("/tasks", body);
        toast.success("Task added");
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md" data-testid="task-form-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="grid place-items-center h-10 w-10 rounded-sm bg-blue-100 text-blue-800">
              <ClipboardList size={20} />
            </div>
            <div>
              <DialogTitle className="font-display text-2xl font-black tracking-tight">
                {isEdit ? "Edit Task" : "New Task"}
              </DialogTitle>
              <DialogDescription>
                e.g. "Deliver 4 items to school" — assign it to a teammate and it shows on their calendar day.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Task</Label>
            <Input
              required
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              placeholder="Deliver 4 items to Riverside school"
              data-testid="task-title-input"
              className="mt-1.5 rounded-sm"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Assignee</Label>
              <Select value={form.assignee_id || "__none__"} onValueChange={(v) => update("assignee_id", v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="task-assignee-select" className="mt-1.5 rounded-sm">
                  <SelectValue placeholder="Anyone" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Unassigned</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Date</Label>
              <Input
                type="date"
                required
                value={form.date}
                onChange={(e) => update("date", e.target.value)}
                data-testid="task-date-input"
                className="mt-1.5 rounded-sm"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Project</Label>
              <Select
                value={form.project_id || "__none__"}
                onValueChange={(v) => update("project_id", v === "__none__" ? "" : v)}
                disabled={lockProject}
              >
                <SelectTrigger data-testid="task-project-select" className="mt-1.5 rounded-sm">
                  <SelectValue placeholder="No project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No project</SelectItem>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Quantity (optional)</Label>
              <Input
                type="number"
                min="0"
                value={form.quantity}
                onChange={(e) => update("quantity", e.target.value)}
                placeholder="e.g. 4 items, 2 boxes"
                data-testid="task-qty-input"
                className="mt-1.5 rounded-sm font-mono"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Where</Label>
            <Input
              value={form.location}
              onChange={(e) => update("location", e.target.value)}
              placeholder="e.g. Roosevelt Elementary, 220 Main St"
              data-testid="task-location-input"
              className="mt-1.5 rounded-sm"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Notes</Label>
            <Textarea
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              rows={2}
              data-testid="task-desc-input"
              className="mt-1.5 rounded-sm"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="task-submit-btn" className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm">
              {busy && <Loader2 size={16} className="animate-spin mr-1.5" />}
              {isEdit ? "Save Task" : "Add Task"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
